import { RefObject, useCallback } from 'react'

import { Cell, flexRender, Row } from '@tanstack/react-table'
import isNil from 'lodash.isnil'
import styled from 'styled-components'
import { twMerge } from 'tailwind-merge'

import { getFontSize, TextSize } from 'src/components/Typography/Text'

import { TableColumnId } from './types'
import { TableCellStickyStyles, TableCellStickyStylesProps } from './util'

const StyledTD = styled.td<
  {
    $horizontalPaddingOverride?: React.CSSProperties['paddingLeft']
    $isLastRow: boolean
    $minWidth?: React.CSSProperties['minWidth']
    $textAlign?: React.CSSProperties['textAlign']
    $verticalAlign?: React.CSSProperties['verticalAlign']
    $width: React.CSSProperties['width']
  } & TableCellStickyStylesProps
>`
  width: ${({ $width }) => $width};
  ${({ $minWidth }) => $minWidth && `min-width: ${$minWidth};`}
  ${({ $verticalAlign }) => `vertical-align: ${$verticalAlign ?? 'middle'};`};
  text-align: ${({ $textAlign }) => $textAlign ?? 'left'};
  padding: ${({ $horizontalPaddingOverride }) =>
    !isNil($horizontalPaddingOverride) ? `8px ${$horizontalPaddingOverride}` : '8px'};
  &:first-child {
    padding-left: ${({ $horizontalPaddingOverride }) =>
      !isNil($horizontalPaddingOverride) ? $horizontalPaddingOverride : '12px'};
  }
  &:last-child {
    padding-right: ${({ $horizontalPaddingOverride }) =>
      !isNil($horizontalPaddingOverride) ? $horizontalPaddingOverride : '12px'};
  }

  ${({ $isLastRow, theme }) =>
    !$isLastRow &&
    `
     border-bottom: solid 1px ${theme.colorSplit};
  `};

  font-family: ${({ theme }) => theme.fontFamily};
  font-size: ${getFontSize(TextSize.Base)}px;
  font-weight: 400;
  color: ${({ theme }) => theme.colorText};

  ${TableCellStickyStyles};
`

type Props<TData> = {
  cell: Cell<TData, unknown>
  dragHandleRef: RefObject<HTMLTableCellElement>
  isFirstRightStickyColumn: boolean
  isLastLeftStickyColumn: boolean
  isLastRow: boolean
  isRowHighlighted: boolean
  isRowReorderable: boolean
  isRowSelectionEnabled: boolean
  isScrolledToLeft: boolean
  isScrolledToRight: boolean
  left: number
  onClick?: (e: React.MouseEvent<HTMLTableCellElement>, row: Row<TData>) => void
  right: number
  stickiness: 'left' | 'right' | 'none'
  verticalAlign: React.CSSProperties['verticalAlign']
}
const TableCell = <TData,>({
  cell,
  dragHandleRef,
  isFirstRightStickyColumn,
  isLastLeftStickyColumn,
  isLastRow,
  isRowHighlighted,
  isRowReorderable,
  isRowSelectionEnabled,
  isScrolledToLeft,
  isScrolledToRight,
  left,
  onClick,
  right,
  stickiness,
  verticalAlign,
}: Props<TData>) => {
  const width = cell.column.columnDef.meta?.width
  const minWidth = cell.column.columnDef.meta?.minWidth

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLTableCellElement>) => {
      onClick?.(e, cell.row)
    },
    [cell.row, onClick]
  )

  return (
    <StyledTD
      key={cell.id}
      $horizontalPaddingOverride={cell.column.columnDef.meta?.dangerouslyOverrideHorizontalPadding}
      $isLastRow={isLastRow}
      $isScrolledToLeft={isScrolledToLeft && isLastLeftStickyColumn}
      $isScrolledToRight={isScrolledToRight && isFirstRightStickyColumn}
      $left={left}
      $minWidth={minWidth}
      $right={right}
      $stickiness={stickiness}
      $textAlign={cell.column.columnDef.meta?.textAlign}
      $verticalAlign={verticalAlign}
      $width={width ? width : 'auto'}
      className={twMerge(
        'bg-white',
        isRowSelectionEnabled ? 'group-hover/table-row:!bg-neutral-50' : '',
        isRowHighlighted ? '!bg-neutral-50' : '',
        cell.row.getIsSelected() ? '!bg-orange-50 group-hover/table-row:!bg-orange-50' : ''
      )}
      onClick={handleClick}
      ref={cell.column.id === TableColumnId.Reorder && isRowReorderable ? dragHandleRef : null}
    >
      {flexRender(cell.column.columnDef.cell, cell.getContext())}
    </StyledTD>
  )
}

export default TableCell
